/**
 * Debug Overlay
 *
 * Automatically detects and visualizes all React components in the DOM.
 * Draws borders and labels over components for easy identification.
 */

import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useDebug } from "./DebugContext";

const DebugOverlay = () => {
  const [componentBounds, setComponentBounds] = useState([]);
  const rafRef = useRef(null);
  const mountedRef = useRef(true);
  const { depthLevel, setMaxDepth } = useDebug();

  useEffect(() => {
    mountedRef.current = true;

    const detectComponents = () => {
      if (!mountedRef.current) return;

      const bounds = [];

      // Find all elements with data-component-name attribute
      const elements = document.querySelectorAll("[data-component-name]");

      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const name = el.getAttribute("data-component-name");

        // Only show visible elements
        if (rect.width > 0 && rect.height > 0) {
          bounds.push({
            name,
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            height: rect.height,
          });
        }
      });

      // Also detect React elements by walking the fiber tree from roots
      const reactRoots = [];
      
      // Find React fibers and walk up to the root
      const rootElement = document.getElementById('root');
      if (rootElement) {
        // Try to get fiber directly from root element first
        let rootFiber = getFiberFromElement(rootElement);
        
        if (!rootFiber) {
          // Try children if root itself doesn't have fiber
          const children = rootElement.querySelectorAll('*');
          for (let i = 0; i < Math.min(10, children.length); i++) {
            rootFiber = getFiberFromElement(children[i]);
            if (rootFiber) break;
          }
        }
        
        if (rootFiber) {
          // Log the starting fiber
          console.log('[Debug] Starting fiber:', rootFiber.type?.name || rootFiber.type, 'depth will be calculated from here');
          
          // Walk up to find the actual root
          let current = rootFiber;
          let stepsUp = 0;
          while (current.return) {
            current = current.return;
            stepsUp++;
          }
          
          console.log(`[Debug] Walked up ${stepsUp} steps to root`);
          console.log('[Debug] Root fiber type:', current.type?.name || current.type, 'tag:', current.tag);
          
          reactRoots.push(current);
        }
      }
      
      console.log(`[Debug] Found ${reactRoots.length} React root(s)`);
      
      const labeledFibers = new WeakSet();
      let componentsFound = 0;
      
      reactRoots.forEach((root) => {
        if (root) {
          const beforeCount = bounds.length;
          // Walk the entire fiber tree from the root
          walkFiberTree(root, bounds, labeledFibers, 0);
          componentsFound += (bounds.length - beforeCount);
        }
      });
      
      console.log(`[Debug] Walked ${reactRoots.length} roots, found ${componentsFound} components`);

      // Calculate max depth and invert depth values so 0 = outermost
      const maxDepthFound = bounds.reduce((maxVal, bound) => Math.max(maxVal, bound.depth), 0);
      
      console.log(`[Debug] Max depth found: ${maxDepthFound}`);
      
      // Log components grouped by depth (before inversion)
      const componentsByDepth = {};
      bounds.forEach(bound => {
        if (!componentsByDepth[bound.depth]) {
          componentsByDepth[bound.depth] = [];
        }
        componentsByDepth[bound.depth].push(bound.name);
      });
      
      console.log('[Debug] Components by depth (before inversion):');
      for (let d = 0; d <= maxDepthFound; d++) {
        const comps = componentsByDepth[d] || [];
        console.log(`  Depth ${d}: ${comps.length > 0 ? comps.join(', ') : '(none)'}`);
      }
      
      // Invert depths: outermost components get depth 0
      bounds.forEach(bound => {
        bound.depth = maxDepthFound - bound.depth;
      });
      
      setMaxDepth(maxDepthFound);
      setComponentBounds(bounds);
    };

    // Walk the React Fiber tree to find all components
    const walkFiberTree = (fiber, bounds, labeledFibers, depth) => {
      if (!fiber || depth > 50) return;
      
      // Log first few components to see what we're encountering
      if (depth <= 5 && fiber.type && typeof fiber.type === 'function') {
        const name = fiber.type.name || fiber.type.displayName || 'Anonymous';
        console.log(`[Debug] Depth ${depth}: Found component ${name}`);
      }
      
      // Check if this fiber is a component
      if (fiber.type && typeof fiber.type === 'function') {
        const componentName = fiber.type.displayName || fiber.type.name;
        
        if (componentName && 
            componentName.length > 2 && 
            !componentName.startsWith('_') && 
            componentName !== 'Component' &&
            !labeledFibers.has(fiber)) {
          
          labeledFibers.add(fiber);
          
          // Collect all DOM elements for this component
          const elements = [];
          collectDOMElements(fiber, elements, new Set());
          
          if (elements.length > 0) {
            // Calculate bounding box that encompasses all elements
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            
            elements.forEach(el => {
              const rect = el.getBoundingClientRect();
              if (rect.width > 0 && rect.height > 0) {
                minX = Math.min(minX, rect.left);
                minY = Math.min(minY, rect.top);
                maxX = Math.max(maxX, rect.right);
                maxY = Math.max(maxY, rect.bottom);
              }
            });
            
            const width = maxX - minX;
            const height = maxY - minY;
            
            if (width > 10 && height > 10 && isFinite(width) && isFinite(height)) {
              bounds.push({
                name: componentName,
                depth: depth,
                top: minY + window.scrollY,
                left: minX + window.scrollX,
                width: width,
                height: height,
              });
            }
          }
        }
      }
      
      // Continue walking the tree
      if (fiber.child) {
        const childDepth = (fiber.type && typeof fiber.type === 'function') ? depth + 1 : depth;
        walkFiberTree(fiber.child, bounds, labeledFibers, childDepth);
      }
      if (fiber.sibling) {
        walkFiberTree(fiber.sibling, bounds, labeledFibers, depth);
      }
    };
    
    // Collect all DOM elements within a fiber component
    const collectDOMElements = (fiber, elements, visited) => {
      if (!fiber || visited.has(fiber)) return;
      visited.add(fiber);
      
      // If this fiber has a DOM element, add it
      if (fiber.stateNode instanceof HTMLElement) {
        elements.push(fiber.stateNode);
      }
      
      // Always walk children to find DOM elements, even through component boundaries
      // This allows wrapper components (like providers) to get bounding boxes
      if (fiber.child) {
        collectDOMElements(fiber.child, elements, visited);
      }
      
      // Also walk siblings
      if (fiber.sibling) {
        collectDOMElements(fiber.sibling, elements, visited);
      }
    };

    const getFiberFromElement = (element) => {
      // React 18 uses __reactFiber$[random string]
      const key = Object.keys(element).find(
        (key) =>
          key.startsWith("__reactFiber") ||
          key.startsWith("__reactInternalInstance") ||
          key.startsWith("__reactProps")
      );
      const fiber = element[key];
      
      if (!fiber && element._reactRootContainer) {
        return element._reactRootContainer._internalRoot?.current;
      }
      
      return fiber;
    };

    // Initial detection
    detectComponents();

    // Update on scroll, resize, and periodically for dynamic content
    const scheduleUpdate = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(detectComponents);
    };

    window.addEventListener("scroll", scheduleUpdate, true);
    window.addEventListener("resize", scheduleUpdate);

    // Poll for dynamic content changes
    const intervalId = setInterval(detectComponents, 1000);

    // MutationObserver for DOM changes
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      mountedRef.current = false;
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
      clearInterval(intervalId);
      observer.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [depthLevel, setMaxDepth]);

  // Group components by name for color consistency
  const getColorForComponent = (name) => {
    const colors = [
      "#ff6b6b",
      "#4ecdc4",
      "#45b7d1",
      "#f9ca24",
      "#6c5ce7",
      "#fd79a8",
      "#fdcb6e",
      "#00b894",
      "#e17055",
      "#74b9ff",
    ];

    const hash = name.split("").reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);

    return colors[Math.abs(hash) % colors.length];
  };

  // Render overlay boxes (filtered by depth level)
  const filteredBounds = componentBounds.filter(bound => bound.depth === depthLevel);
  
  return createPortal(
    <div className="debug-overlay">
      {filteredBounds.map((bound, index) => (
        <div
          key={`${bound.name}-${index}`}
          className="debug-box"
          style={{
            position: "absolute",
            top: bound.top,
            left: bound.left,
            width: bound.width,
            height: bound.height,
            border: `2px solid ${getColorForComponent(bound.name)}`,
            pointerEvents: "none",
            zIndex: 999999,
            boxSizing: "border-box",
          }}
        >
          <div
            className="debug-label"
            style={{
              position: "absolute",
              top: -2,
              left: -2,
              background: getColorForComponent(bound.name),
              color: "#fff",
              padding: "2px 6px",
              fontSize: "10px",
              fontFamily: "monospace",
              fontWeight: "bold",
              lineHeight: "1.2",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              borderRadius: "2px 0 2px 0",
            }}
          >
            {bound.name}
          </div>
        </div>
      ))}
    </div>,
    document.body
  );
};

export default DebugOverlay;
